// Lightweight markdown renderer used for AI chat responses, prompt
// previews, and any inline markdown content. Supports headers, bold,
// bullet lists, numbered lists, and basic tables.
//
// Uses theme tokens directly so the rendering matches the rest of the
// app's styling.

import { ReactNode } from "react";
import { T } from "../theme";

export const renderMarkdown = (text: string): ReactNode => {
  if (!text) return null;
  const lines = text.split('\n');
  const elements: ReactNode[] = [];
  let tableRows: any[] = [];
  let inTable = false;

  const processInline = (str: string): ReactNode[] => {
    const parts: ReactNode[] = [];
    let remaining = str;
    let key = 0;
    while (remaining) {
      const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
      if (boldMatch) {
        const idx = remaining.indexOf(boldMatch[0]);
        if (idx > 0) parts.push(<span key={key++}>{remaining.slice(0, idx)}</span>);
        parts.push(<strong key={key++} style={{ fontWeight: 700 }}>{boldMatch[1]}</strong>);
        remaining = remaining.slice(idx + boldMatch[0].length);
      } else {
        parts.push(<span key={key++}>{remaining}</span>);
        remaining = '';
      }
    }
    return parts;
  };

  const flushTable = () => {
    if (tableRows.length < 2) {
      tableRows.forEach((r: any, i: number) => elements.push(<div key={`tr-${elements.length}-${i}`}>{r}</div>));
      tableRows = [];
      inTable = false;
      return;
    }
    const parseRow = (row: string) => row.split('|').map(cell => cell.trim()).filter(cell => cell !== '');
    const headers = parseRow(tableRows[0]);
    const dataRows = tableRows.slice(2).map(parseRow);

    elements.push(
      <div key={`table-${elements.length}`} style={{ overflowX: 'auto', marginBottom: 12 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${T.brd}` }}>
              {headers.map((h: any, i: number) => <th key={i} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 700, color: T.tx, fontSize: 10, textTransform: 'uppercase', letterSpacing: '.03em' }}>{processInline(h)}</th>)}
            </tr>
          </thead>
          <tbody>
            {dataRows.map((row, ri) => (
              <tr key={ri} style={{ borderBottom: `1px solid ${T.brdSubtle}` }}>
                {row.map((cell, ci) => <td key={ci} style={{ padding: '5px 10px', color: T.tx, fontSize: 12 }}>{processInline(cell)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
    tableRows = [];
    inTable = false;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (line.includes('|') && line.trim().startsWith('|')) {
      inTable = true;
      tableRows.push(line);
      continue;
    } else if (inTable) {
      flushTable();
    }

    if (line.startsWith('### ')) {
      elements.push(<div key={i} style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginTop: 12, marginBottom: 4 }}>{processInline(line.slice(4))}</div>);
    } else if (line.startsWith('## ')) {
      elements.push(<div key={i} style={{ fontSize: 14, fontWeight: 700, color: T.tx, marginTop: 14, marginBottom: 6 }}>{processInline(line.slice(3))}</div>);
    } else if (line.startsWith('# ')) {
      elements.push(<div key={i} style={{ fontSize: 16, fontWeight: 800, color: T.tx, marginTop: 16, marginBottom: 8 }}>{processInline(line.slice(2))}</div>);
    } else if (line.match(/^[-*]\s/)) {
      elements.push(<div key={i} style={{ paddingLeft: 16, marginBottom: 3, position: 'relative' }}><span style={{ position: 'absolute', left: 4, color: T.txD }}>·</span>{processInline(line.slice(2))}</div>);
    } else if (line.match(/^\d+\.\s/)) {
      const num = line.match(/^(\d+)\.\s/)?.[1] || '0';
      elements.push(<div key={i} style={{ paddingLeft: 20, marginBottom: 3, position: 'relative' }}><span style={{ position: 'absolute', left: 0, color: T.txD, fontWeight: 600, fontSize: 11 }}>{num}.</span>{processInline(line.replace(/^\d+\.\s/, ''))}</div>);
    } else if (line.trim() === '') {
      elements.push(<div key={i} style={{ height: 6 }} />);
    } else {
      elements.push(<div key={i} style={{ marginBottom: 2 }}>{processInline(line)}</div>);
    }
  }
  if (inTable) flushTable();

  return <div>{elements}</div>;
};
